import { GpsType } from '../../../../../../../global/components/layout/layout-models';
import { getSummaryPlan, postRsvpPlan, validateRsvpPlan } from '../../../../../../../global/ducks/benadmin-api';
import { YesNoIndicators } from '../../../../../../../global/models/api-model';
import { FormTypes } from '../../../../../../account-info/models/form-models';
import { ArcRoutes } from '../../../../../constants/routes';
import { DocuSignRequest } from '../../../../docusign/models/docusign-models';
import { dispatchSetPlanSummary } from '../../../institution-slice';
import { PensionPlanTypes } from '../../../plan-history/plan-history-models';
import { ContributionAmountType, EmployerContributionStatusType, IContributionOverride, LayDcPlanAdoptionContentModel, LayDcPlanFormModel, PlanSubmitRequest } from '../../models/plan-adoption-form-models';
import { IPlanSubmitRequest } from '../../models/plan-adoption-requests';
import { PlanAdoptionRoutes } from '../../routes';
import { LayDcReviewWizard } from '../lay-dc/lay-dc-review-wizard';

export class RsvpReviewWizard extends LayDcReviewWizard {

    getFormContentModel(): LayDcPlanAdoptionContentModel {

        const institutionSettings = this.epiContent?.institutionSettingsState?.epiContent;
        const content = super.getFormContentModel();
        const requiresIndemnification = this.isIndemnificationRequired();

        return {
            ...content,
            gpsType: GpsType.RsvpAdoptionReview,
            pageTitle: institutionSettings?.rsvpReviewTitle,
            adoptDetailsTitle: institutionSettings?.adoptLayRSVPDetailsTitle,
            PageDescription: institutionSettings?.adoptRSVPReviewDescription,
            submitLabel: institutionSettings?.adoptLayRSVPReviewSubmitLabel,
            cancelLabel: institutionSettings?.adoptLayRSVPReviewCancelLabel,
            hasEmployerContributionStatus: true,
            hasMemberType: true,
            isEmployerContributionRequired: false,
            adoptEmployerContributionText: institutionSettings?.adoptRSVPEmployerContributionText,
            docuSignSubject: institutionSettings?.adoptRSVPDocuSignSubject,
            docuSignFinishText: institutionSettings?.adoptRSVPDocusignFinishText,
            docuSignTemplateId: requiresIndemnification ? institutionSettings?.adoptIndemnifyRSVPDocuSignTemplateId : institutionSettings?.adoptRSVPDocuSignTemplateId,
            adoptMinScheduleHoursHelperText: institutionSettings?.adoptRSVPMinScheduleHoursHelperText,
            adoptEmployeeSalaryDeferText: institutionSettings?.adoptRSVPEmployeeSalaryDeferText,
            adoptMultipleContributionDescription: institutionSettings?.adoptRSVPMultipleContributionDescription,
        };
    }

    getPreviousStepUrl() {
        return `${this.wizardOptions?.featureRootPath}${PlanAdoptionRoutes.AdoptionDetails}`;
    }

    getNextStepUrl() {
        return `${this.wizardOptions?.institutionRootPath}${ArcRoutes.PlanSummary}`;
    }

    onSuccessQueryParams() {
        return `?formType=${this.wizardOptions?.formType}&form=${PensionPlanTypes.Rsvp}`;
    }

    createSubmitModel(formValue: LayDcPlanFormModel): IPlanSubmitRequest {

        const contributionOverride: IContributionOverride[] = formValue?.participants?.map((participant) => this.mapParticipantToContributionOverride(participant));
        const filledContributionOverride = contributionOverride?.filter((override) => override?.employee_name || override?.contribution_type);

        const request: IPlanSubmitRequest = {
            rsvp_plan: {
                plan_effective_date: formValue?.adoption_date,
                latest_amendment_attributes: {
                    min_scheduled_hours: Number(formValue?.minimum_scheduleds_hours)?.toFixed(0),
                    eligible_population: formValue?.member_type,
                    contribution_type: formValue?.employer_contribution_status,
                    employer_override_allowed: formValue?.allow_multiple_contributions ? 'Y' : 'N',
                },
                docusign_envelope_id: formValue.envelopeId,
            },
            qcco_status: {
                supported_by_church: formValue?.qccoQuestion1 === YesNoIndicators.Yes ? 'Y' : 'N',
                offers_services_to_public: formValue?.qccoQuestion2 === YesNoIndicators.Yes ? 'Y' : 'N',
                receives_gov_support: formValue?.qccoQuestion3 === YesNoIndicators.Yes ? 'Y' : 'N',
            },
        };

        if (formValue?.employer_contribution_status === EmployerContributionStatusType.Scheduled) {
            request.rsvp_plan.latest_amendment_attributes.employer_base = formValue?.employer_base_contribution_amount_type === ContributionAmountType.Percentage ? Number(formValue?.employer_base_contribution_percentage)?.toFixed(0) : Number(formValue?.employer_base_contribution_amount)?.toFixed(0);
            request.rsvp_plan.latest_amendment_attributes.employer_match = formValue?.employer_match_contribution_amount_type === ContributionAmountType.Percentage ? Number(formValue?.employer_match_contribution_percentage)?.toFixed(0) : Number(formValue?.employer_match_contribution_amount)?.toFixed(0);
            request.rsvp_plan.latest_amendment_attributes.employer_base_type = formValue?.employer_base_contribution_amount_type;
            request.rsvp_plan.latest_amendment_attributes.employer_match_type = formValue?.employer_match_contribution_amount_type;
        }

        if (filledContributionOverride?.length > 0) {
            request.rsvp_plan.contribution_overrides = filledContributionOverride;
        }

        return request;
    }

    onSuccess(request: PlanSubmitRequest) {
        this.dispatch(getSummaryPlan(request.clientId, dispatchSetPlanSummary(this.dispatch)));
        super.onSuccess(request);
    }

    onSubmit(request: PlanSubmitRequest) {
        const payload = this.createSubmitModel(request?.values as LayDcPlanFormModel);
        this.dispatch(postRsvpPlan(request?.clientId, payload, () => this.onSuccess(request)));
    }

    onValidateRequest(request: PlanSubmitRequest, onValidationSucess?: (request: PlanSubmitRequest) => void, onFailure?: () => any) {
        const payload = this.createSubmitModel(request?.values as LayDcPlanFormModel);

        this.dispatch(validateRsvpPlan(
            request?.clientId,
            payload,
            () => onValidationSucess(request),
            onFailure),
        );
    }

    // eslint-disable-next-line class-methods-use-this
    getFormType(): FormTypes {
        return FormTypes.ADOPT_INSTITUTION_PLAN_RSVP;
    }

    prepareDocuSignRequest(): DocuSignRequest {

        const defaultRequest = super.prepareDocuSignRequest();

        // TODO: Map the custom text fields.

        const docuSignRequest: DocuSignRequest = {
            ...defaultRequest,
        };

        return docuSignRequest;
    }

    // eslint-disable-next-line class-methods-use-this
    isComplyEpiscopalChurchLayPensionSystemQuestionRequired(): boolean {
        return true;
    }
}